// The marketplace shell: Installed / Available tabs over the SAME PluginSystem
// the app uses, plus an inline detail page. Available = source listings not yet
// installed; Installed = host.list() with live enabled/disabled state. Every
// state change re-reads the host and the source so the lists stay in sync. The
// detail page carries the disclosure + actions; install/enable/disable/uninstall
// all route through installFlow.

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { PluginSystem } from '../plugins/setup';
import type { InstalledPlugin } from '../plugins/runtime';
import type { PluginDetail as Detail, PluginSource, PluginSummary } from './source';
import type { InstallFlow } from './installFlow';
import { computeAccessBadge } from '../plugins/accessBadge';
import { PluginList } from './PluginList';
import { PluginDetail } from './PluginDetail';

/** Build a detail page from an INSTALLED plugin's own manifest, for plugins that
 *  are not in the curated source (e.g. the bundled example). Lets every installed
 *  plugin open its detail and be enabled/disabled/uninstalled. */
function detailFromInstalled(p: InstalledPlugin): Detail {
  const m = p.manifest;
  const caps = (m.permissions ?? []).map((perm) => perm.capability);
  return {
    id: m.id,
    name: m.name,
    version: m.version,
    publisher: m.publisher,
    description: m.description,
    firstParty: true,
    accessBadge: computeAccessBadge(caps),
    manifest: m,
    readme: m.readme ?? `# ${m.name}\n\n${m.description ?? ''}`,
    resolveAsset: () => null,
    install: p,
  };
}

export interface MarketplacePanelProps {
  system: PluginSystem;
  source: PluginSource;
  flow: InstallFlow;
  onOpenLink?(href: string): void;
}

type Tab = 'available' | 'installed';

/** Case-insensitive, all-terms-must-match filter over a summary's text fields. */
function matchesQuery(s: PluginSummary, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = [s.name, s.publisher, s.description ?? '', ...(s.keywords ?? []), ...(s.categories ?? [])]
    .join(' ')
    .toLowerCase();
  return terms.every((t) => haystack.includes(t));
}

/** Build a summary for an installed plugin from its manifest + granted state. */
function installedSummary(system: PluginSystem, id: string, name: string, publisher: string, version: string, description?: string): PluginSummary {
  const perms = system.host.list().find((p) => p.manifest.id === id)?.manifest.permissions ?? [];
  const granted = perms.map((p) => p.capability).filter((c) => system.broker.query(id, c) === 'granted');
  return {
    id, name, version, publisher, description,
    firstParty: true,
    accessBadge: computeAccessBadge(granted),
  };
}

export function MarketplacePanel({ system, source, flow, onOpenLink }: MarketplacePanelProps): ReactElement {
  const [tab, setTab] = useState<Tab>('available');
  const [available, setAvailable] = useState<PluginSummary[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [query, setQuery] = useState('');
  const [, bump] = useState(0);

  const refresh = useCallback(() => {
    void source.listAvailable().then(setAvailable);
    bump((n) => n + 1);
  }, [source]);

  useEffect(() => refresh(), [refresh]);
  useEffect(() => system.broker.onDidChange(() => bump((n) => n + 1)), [system]);

  const installedIds = new Set(system.host.list().map((p) => p.manifest.id));
  const availableNotInstalled = available.filter(
    (s) => !installedIds.has(s.id) && matchesQuery(s, query),
  );
  const installed: PluginSummary[] = system.host
    .list()
    .map((p) =>
      installedSummary(system, p.manifest.id, p.manifest.name, p.manifest.publisher, p.manifest.version, p.manifest.description),
    )
    .filter((s) => matchesQuery(s, query));

  const open = useCallback(
    (id: string) => {
      // Prefer the curated source detail (richer README/assets) when present;
      // fall back to the installed plugin's own manifest (e.g. the bundled
      // example, which is installed but not in the source) so it stays manageable.
      void source
        .getDetail(id)
        .then(setDetail)
        .catch(() => {
          const installed = system.host.list().find((p) => p.manifest.id === id);
          if (installed) setDetail(detailFromInstalled(installed));
        });
    },
    [source, system],
  );

  if (detail) {
    const isInstalled = installedIds.has(detail.id);
    return (
      <div className="mp-marketplace">
        <PluginDetail
          detail={detail}
          installed={isInstalled}
          enabled={isInstalled ? system.host.isEnabled(detail.id) : false}
          flow={flow}
          onOpenLink={onOpenLink}
          onBack={() => setDetail(null)}
          onChanged={() => {
            refresh();
            setDetail(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="mp-marketplace">
      <div className="mp-marketplace__tabs" role="tablist" aria-label="Marketplace">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'available'}
          className="mp-marketplace__tab"
          onClick={() => setTab('available')}
        >
          Available
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'installed'}
          className="mp-marketplace__tab"
          onClick={() => setTab('installed')}
        >
          Installed
        </button>
      </div>

      <input
        type="search"
        className="mp-marketplace__search"
        placeholder="Search plugins…"
        aria-label="Search plugins"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {tab === 'available' ? (
        <PluginList
          label="Available"
          items={availableNotInstalled}
          onOpen={open}
          emptyText="No plugins available."
        />
      ) : (
        <PluginList
          label="Installed"
          items={installed}
          onOpen={open}
          statusFor={(id) => (system.host.isEnabled(id) ? 'Enabled' : 'Disabled')}
          emptyText="No plugins installed."
        />
      )}
    </div>
  );
}
